import { MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Reusable filter bar for invoice tables.
 *
 * Props:
 *  - items: list of records (used to build the month dropdown)
 *  - monthFields: list of fields to choose from, e.g. ["mass_start_date","final_expected_payment_date"]
 *  - search/setSearch, filterMonth/setFilterMonth, filterMonthField/setFilterMonthField, filterStatus/setFilterStatus
 */
const MONTH_NAMES = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function uniqueMonths(items, field) {
  const set = new Set();
  for (const it of items) {
    const v = it[field];
    if (v && typeof v === "string" && v.length >= 7) set.add(v.slice(0, 7));
  }
  return Array.from(set).sort();
}

function formatYM(ym) {
  if (!ym) return ym;
  const [y, m] = ym.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

export default function FilterBar({
  items,
  search, setSearch,
  filterMonth, setFilterMonth,
  filterMonthField, setFilterMonthField,
  filterStatus, setFilterStatus,
  monthFieldOptions = [
    { value: "mass_start_date", label: "Maßnahme-Start" },
    { value: "final_expected_payment_date", label: "Erwarteter Eingang" },
  ],
}) {
  const months = uniqueMonths(items, filterMonthField);
  const active = !!(search || (filterMonth && filterMonth !== "all") || filterStatus !== "all");

  const clearAll = () => {
    setSearch("");
    setFilterMonth("");
    setFilterStatus("all");
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4" data-testid="filter-bar">
      <div className="flex flex-wrap items-end gap-3">
        {/* Suche */}
        <div className="flex-1 min-w-[180px]">
          <div className="label-eyebrow mb-1.5">Suche</div>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Teilnehmer-Name …"
              className="field !pl-8"
              data-testid="filter-search"
            />
          </div>
        </div>

        {/* Monat */}
        <div className="min-w-[160px]">
          <div className="label-eyebrow mb-1.5">Monat</div>
          <select
            value={filterMonth || "all"}
            onChange={(e) => setFilterMonth(e.target.value === "all" ? "" : e.target.value)}
            className="field"
            data-testid="filter-month"
          >
            <option value="all">Alle Monate</option>
            {months.map((m) => (
              <option key={m} value={m}>{formatYM(m)}</option>
            ))}
          </select>
        </div>

        {/* Monat-Feld (wonach filtern wir) */}
        {monthFieldOptions.length > 1 && (
          <div className="min-w-[160px]">
            <div className="label-eyebrow mb-1.5">Monat von</div>
            <select
              value={filterMonthField}
              onChange={(e) => setFilterMonthField(e.target.value)}
              className="field"
              data-testid="filter-month-field"
            >
              {monthFieldOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Status */}
        <div className="min-w-[140px]">
          <div className="label-eyebrow mb-1.5">Status</div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="field"
            data-testid="filter-status"
          >
            <option value="all">Alle</option>
            <option value="open">Offen</option>
            <option value="partial">Teilweise</option>
            <option value="paid">Bezahlt</option>
          </select>
        </div>

        {/* Reset */}
        {active && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1.5 text-xs text-[#5B6371] hover:text-[#0AA09E] px-3 py-2"
            data-testid="filter-clear"
          >
            <X size={14} /> Filter zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
}
